import { RotateCcw } from 'lucide-react'
import type React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import type { CalculatedZone, CalculatedZones, ZoneModel } from '../../types/zones'
import { DEFAULT_HR_ZONES, DEFAULT_PACE_ZONES, DEFAULT_POWER_ZONES } from '../../types/zones'
import { formatZoneRange } from '../../utils/zoneCalculations'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
`

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
`

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ $active, theme }) => ($active ? theme.colors.cardBackground : 'transparent')};
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.text : theme.colors.textSecondary)};
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.cardBackground};
    color: ${({ theme }) => theme.colors.text};
  }
`

const Content = styled.div`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.lg};
  overflow-y: auto;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.md};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`

const ZoneList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const ZoneItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.border};
  }
`

const ZoneColorIndicator = styled.div<{ $color: string }>`
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ $color }) => $color};
  border: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`

const ZoneInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

const ZoneName = styled.div<{ $editable?: boolean }>`
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  cursor: ${({ $editable }) => ($editable ? 'pointer' : 'default')};
  padding: 2px 4px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: background 0.2s;

  &:hover {
    background: ${({ $editable, theme }) => ($editable ? theme.colors.surfaceHover : 'transparent')};
  }
`

const ZoneNameInput = styled.input`
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 2px 4px;
  width: 100%;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const ZoneRange = styled.div<{ $editable?: boolean }>`
  font-size: ${({ theme }) => theme.typography.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace;
  cursor: ${({ $editable }) => ($editable ? 'pointer' : 'default')};
  padding: 2px 4px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: background 0.2s;

  &:hover {
    background: ${({ $editable, theme }) => ($editable ? theme.colors.border : 'transparent')};
  }
`

const ZoneRangeInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};

  &:hover {
      background: ${({ theme }) => theme.colors.surfaceHover};
  }
`

const ZoneRangeInput = styled.input`
  font-size: ${({ theme }) => theme.typography.xs};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 2px 4px;
  width: 50px;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const ZoneRangeLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`

const ZoneExplanation = styled.div`
    font-size: ${({ theme }) => theme.typography.xs};
    color: ${({ theme }) => theme.colors.textMuted};
`

const ResetButton = styled.button`
  padding: ${({ theme }) => theme.spacing.xs};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  font-size: ${({ theme }) => theme.typography.xs};

  &:hover {
    background: ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.text};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.sm};
`

const ColorPickerInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`

interface ThresholdValues {
  vt1Bpm: number | null
  vt2Bpm: number | null
  maxHrBpm: number | null
  vt1PaceSeconds: number | null
  vt2PaceSeconds: number | null
  vt1PowerWatts: number | null
  vt2PowerWatts: number | null
}

interface ZonePreviewProps {
  zoneModels: ZoneModel[]
  calculatedZones: CalculatedZones
  thresholdValues?: ThresholdValues
  onZoneModelChange?: (type: 'heart_rate' | 'pace' | 'power', zoneModel: ZoneModel) => void
  editable?: boolean
}

export const ZonePreview: React.FC<ZonePreviewProps> = ({
  zoneModels,
  calculatedZones,
  thresholdValues,
  onZoneModelChange,
  editable = true,
}) => {
  const [activeTab, setActiveTab] = useState<'hr' | 'pace' | 'power'>('hr')
  const [currentType, setCurrentType] = useState<'heart_rate' | 'pace' | 'power'>('heart_rate')
  const [currentZoneModel, setCurrentZoneModel] = useState<ZoneModel | undefined>(undefined)
  const [defaultZoneModel, setDefaultZoneModel] = useState<ZoneModel | undefined>(undefined)
  const [editingNameId, setEditingNameId] = useState<string | null>(null)
  const [editingRangeId, setEditingRangeId] = useState<string | null>(null)
  const [editNameValue, setEditNameValue] = useState('')
  const [editMinValue, setEditMinValue] = useState('')
  const [editMaxValue, setEditMaxValue] = useState('')

  const getCurrentZones = (): CalculatedZone[] => {
    switch (activeTab) {
      case 'hr':
        return calculatedZones.hr
      case 'pace':
        return calculatedZones.pace
      case 'power':
        return calculatedZones.power
      default:
        return []
    }
  }

  const getCurrentZoneModel = useCallback(
    (type: 'heart_rate' | 'pace' | 'power'): ZoneModel | undefined => {
      switch (type) {
        case 'heart_rate':
          return zoneModels.find((z) => z.type === 'heart_rate')
        case 'pace':
          return zoneModels.find((z) => z.type === 'pace')
        case 'power':
          return zoneModels.find((z) => z.type === 'power')
        default:
          return undefined
      }
    },
    [zoneModels]
  )

  const getDefaultZoneModel = useCallback((type: 'heart_rate' | 'pace' | 'power'): ZoneModel => {
    switch (type) {
      case 'heart_rate':
        return DEFAULT_HR_ZONES
      case 'pace':
        return DEFAULT_PACE_ZONES
      case 'power':
        return DEFAULT_POWER_ZONES
      default:
        return DEFAULT_HR_ZONES
    }
  }, [])

  const formatType = (type: 'heart_rate' | 'pace' | 'power'): string => {
    switch (type) {
      case 'heart_rate':
        return 'Heart Rate'
      case 'pace':
        return 'Pace'
      case 'power':
        return 'Power'
      default:
        return 'Unknown'
    }
  }

  const handleColorChange = (zoneId: string, newColor: string) => {
    if (!onZoneModelChange) return

    if (!currentZoneModel) return

    const updatedZones = currentZoneModel.zones.map((zone) =>
      zone.id === zoneId ? { ...zone, color: newColor } : zone
    )

    onZoneModelChange(currentType, {
      ...currentZoneModel,
      zones: updatedZones,
    })
  }

  useEffect(() => {
    let currentType: 'heart_rate' | 'pace' | 'power' = 'heart_rate'
    switch (activeTab) {
      case 'hr':
        currentType = 'heart_rate'
        break
      case 'pace':
        currentType = 'pace'
        break
      case 'power':
        currentType = 'power'
        break
      default:
        currentType = 'heart_rate'
    }
    setCurrentType(currentType)
  }, [activeTab])

  useEffect(() => {
    setCurrentZoneModel(getCurrentZoneModel(currentType))
  }, [currentType, getCurrentZoneModel])

  useEffect(() => {
    setDefaultZoneModel(getDefaultZoneModel(currentType))
  }, [currentType, getDefaultZoneModel])

  const handleNameClick = (zone: CalculatedZone) => {
    if (!editable) return
    setEditingNameId(zone.id)
    setEditNameValue(zone.name)
  }

  const handleNameSave = () => {
    if (!onZoneModelChange || !editingNameId) return
    if (!currentZoneModel) return

    const updatedZones = currentZoneModel.zones.map((zone) =>
      zone.id === editingNameId ? { ...zone, name: editNameValue } : zone
    )

    onZoneModelChange(currentType, {
      ...currentZoneModel,
      zones: updatedZones,
    })

    setEditingNameId(null)
    setEditNameValue('')
  }

  const handleNameCancel = () => {
    setEditingNameId(null)
    setEditNameValue('')
  }

  const handleRangeClick = (zone: CalculatedZone) => {
    if (!editable) return
    setEditingRangeId(zone.id)
    setEditMinValue(zone.minValue?.toString() || '')
    setEditMaxValue(zone.maxValue?.toString() || '')
  }

  const calculatePercentFromValue = (value: number, type: 'heart_rate' | 'pace' | 'power'): number => {
    if (!thresholdValues) return 0

    switch (type) {
      case 'heart_rate':
        // For HR, we use VT2 as the reference (100%)
        if (thresholdValues.vt2Bpm) {
          return (value / thresholdValues.vt2Bpm) * 100
        }
        if (thresholdValues.vt1Bpm) {
          return (value / thresholdValues.vt1Bpm) * 100
        }
        return 0

      case 'pace':
        // For pace, we use VT2 as the reference (100%)
        // Note: for pace, higher values = slower, so percentage works inversely
        if (thresholdValues.vt2PaceSeconds) {
          return (value / thresholdValues.vt2PaceSeconds) * 100
        }
        if (thresholdValues.vt1PaceSeconds) {
          return (value / thresholdValues.vt1PaceSeconds) * 100
        }
        return 0

      case 'power':
        // For power, we use VT2 as the reference (100%)
        if (thresholdValues.vt2PowerWatts) {
          return (value / thresholdValues.vt2PowerWatts) * 100
        }
        if (thresholdValues.vt1PowerWatts) {
          return (value / thresholdValues.vt1PowerWatts) * 100
        }
        return 0

      default:
        return 0
    }
  }

  const handleRangeSave = () => {
    if (!onZoneModelChange || !editingRangeId) return
    if (!currentZoneModel) return

    const minValue = parseFloat(editMinValue)
    const maxValue = parseFloat(editMaxValue)

    if (Number.isNaN(minValue) || Number.isNaN(maxValue)) {
      handleRangeCancel()
      return
    }

    // Convert absolute values to percentages
    const minPercent = calculatePercentFromValue(minValue, currentType)
    const maxPercent = calculatePercentFromValue(maxValue, currentType)

    const updatedZones = currentZoneModel.zones.map((zone) =>
      zone.id === editingRangeId ? { ...zone, minPercent, maxPercent, description: 'Manual' } : zone
    )

    onZoneModelChange(currentType, {
      ...currentZoneModel,
      zones: updatedZones,
    })

    setEditingRangeId(null)
    setEditMinValue('')
    setEditMaxValue('')
  }

  const handleRangeCancel = () => {
    setEditingRangeId(null)
    setEditMinValue('')
    setEditMaxValue('')
  }

  const getUnitLabel = (type: 'heart_rate' | 'pace' | 'power'): string => {
    switch (type) {
      case 'heart_rate':
        return 'bpm'
      case 'pace':
        return 'min/km'
      case 'power':
        return 'W'
      default:
        return ''
    }
  }

  const handleResetZone = (zoneId: string) => {
    if (!onZoneModelChange) return
    if (!currentZoneModel) return

    const defaultModel = getDefaultZoneModel(currentType)
    const defaultZone = defaultModel.zones.find((z) => z.id === zoneId)

    if (!defaultZone) return

    const updatedZones = currentZoneModel.zones.map((zone) =>
      zone.id === zoneId
        ? {
            ...zone,
            name: defaultZone.name,
            minPercent: defaultZone.minPercent,
            maxPercent: defaultZone.maxPercent,
            description: defaultZone.description,
          }
        : zone
    )

    onZoneModelChange(currentType, {
      ...currentZoneModel,
      zones: updatedZones,
    })
  }

  const zones = getCurrentZones()
  const hasHeartRate = calculatedZones?.hr?.some((zone) => zone.minValue !== null && zone.maxValue !== null) ?? false
  const hasPaceZones = calculatedZones?.pace?.some((zone) => zone.minValue !== null && zone.maxValue !== null) ?? false
  const hasPowerZones =
    calculatedZones?.power?.some((zone) => zone.minValue !== null && zone.maxValue !== null) ?? false

  return (
    <Container>
      <TabContainer>
        {hasHeartRate && (
          <Tab $active={activeTab === 'hr'} onClick={() => setActiveTab('hr')}>
            Heart Rate
          </Tab>
        )}
        {hasPaceZones && (
          <Tab $active={activeTab === 'pace'} onClick={() => setActiveTab('pace')}>
            Pace
          </Tab>
        )}
        {hasPowerZones && (
          <Tab $active={activeTab === 'power'} onClick={() => setActiveTab('power')}>
            Power
          </Tab>
        )}
      </TabContainer>

      <Content>
        <SectionHeader>
          <SectionTitle>
            {zones?.length}-Zone {formatType(currentType)}
          </SectionTitle>
        </SectionHeader>

        {zones && zones.length === 0 ? (
          <EmptyState>No zones configured. Add threshold values to see zone calculations.</EmptyState>
        ) : (
          <ZoneList>
            {zones?.length &&
              zones?.map((zone, i) => (
                <ZoneItem key={zone.id}>
                  <ZoneColorIndicator
                    $color={zone.color}
                    onClick={() => {
                      if (editable) {
                        document.getElementById(`color-picker-${zone.id}`)?.click()
                      }
                    }}
                  >
                    {editable && (
                      <ColorPickerInput
                        id={`color-picker-${zone.id}`}
                        type="color"
                        value={zone.color}
                        onChange={(e) => handleColorChange(zone.id, e.target.value)}
                      />
                    )}
                  </ZoneColorIndicator>

                  <ZoneInfo>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {editingNameId === zone.id ? (
                        <ZoneNameInput
                          value={editNameValue}
                          onChange={(e) => setEditNameValue(e.target.value)}
                          onBlur={handleNameSave}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleNameSave()
                            if (e.key === 'Escape') handleNameCancel()
                          }}
                          autoFocus
                        />
                      ) : (
                        <ZoneName $editable={editable} onClick={() => handleNameClick(zone)}>
                          {zone.name}
                        </ZoneName>
                      )}
                      {editable &&
                        (zone.description === 'Manual' || zone.name !== defaultZoneModel?.zones[i]?.name) && (
                          <ResetButton onClick={() => handleResetZone(zone.id)} title="Reset to default">
                            <RotateCcw size={12} />
                          </ResetButton>
                        )}
                    </div>

                    {editingRangeId === zone.id ? (
                      <ZoneRangeInputContainer>
                        <ZoneRangeInput
                          type="number"
                          step={currentType === 'heart_rate' ? '1' : currentType === 'power' ? '1' : '0.1'}
                          value={editMinValue}
                          onChange={(e) => setEditMinValue(e.target.value)}
                          onBlur={handleRangeSave}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRangeSave()
                            if (e.key === 'Escape') handleRangeCancel()
                          }}
                          autoFocus
                        />
                        <ZoneRangeLabel>{getUnitLabel(currentType)}</ZoneRangeLabel>
                        <ZoneRangeLabel>-</ZoneRangeLabel>
                        <ZoneRangeInput
                          type="number"
                          step={currentType === 'heart_rate' ? '1' : currentType === 'power' ? '1' : '0.1'}
                          value={editMaxValue}
                          onChange={(e) => setEditMaxValue(e.target.value)}
                          onBlur={handleRangeSave}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRangeSave()
                            if (e.key === 'Escape') handleRangeCancel()
                          }}
                        />
                        <ZoneRangeLabel>{getUnitLabel(currentType)}</ZoneRangeLabel>
                      </ZoneRangeInputContainer>
                    ) : (
                      <ZoneRange $editable={editable} onClick={() => handleRangeClick(zone)}>
                        {formatZoneRange(zone.minValue, zone.maxValue, currentType)}
                      </ZoneRange>
                    )}

                    {/* Zone Explanation - shows description if manually edited, otherwise shows defaults */}
                    {zone.description === 'Manual' ? (
                      <ZoneExplanation>Manual</ZoneExplanation>
                    ) : (
                      <>
                        {/* HR Zones */}
                        <ZoneExplanation>{zone.id === 'hr_z1' && '0 to VT1 - 5 bpm'}</ZoneExplanation>
                        <ZoneExplanation>{zone.id === 'hr_z2' && 'VT1 - 5 bpm to VT1'}</ZoneExplanation>
                        <ZoneExplanation>{zone.id === 'hr_z3' && 'VT1 to VT2 - 5 bpm'}</ZoneExplanation>
                        <ZoneExplanation>{zone.id === 'hr_z4' && 'VT2 - 5 bpm to VT2'}</ZoneExplanation>
                        <ZoneExplanation>{zone.id === 'hr_z5' && 'VT2 to max HR'}</ZoneExplanation>

                        {/* Pace Zones */}
                        <ZoneExplanation>{zone.id === 'pace_z1' && 'slower than VT1 + 20%'}</ZoneExplanation>
                        <ZoneExplanation>{zone.id === 'pace_z2' && 'VT1 to VT1 + 10%'}</ZoneExplanation>
                        <ZoneExplanation>{zone.id === 'pace_z3' && 'VT1 - 5% to VT2 + 5%'}</ZoneExplanation>
                        <ZoneExplanation>{zone.id === 'pace_z4' && 'VT2 - 5% to VT2'}</ZoneExplanation>
                        <ZoneExplanation>{zone.id === 'pace_z5' && 'VT2 - 10% to VT2 - 5%'}</ZoneExplanation>
                        <ZoneExplanation>{zone.id === 'pace_z6' && 'faster than VT2 - 10%'}</ZoneExplanation>

                        {/* Power Zones */}
                        <ZoneExplanation>
                          {zone.id === 'power_z1' && `${zone.minPercent}% - ${zone.maxPercent}%`}
                        </ZoneExplanation>
                        <ZoneExplanation>
                          {zone.id === 'power_z2' && `${zone.minPercent}% - ${zone.maxPercent}%`}
                        </ZoneExplanation>
                        <ZoneExplanation>
                          {zone.id === 'power_z3' && `${zone.minPercent}% - ${zone.maxPercent}%`}
                        </ZoneExplanation>
                        <ZoneExplanation>
                          {zone.id === 'power_z4' && `${zone.minPercent}% - ${zone.maxPercent}%`}
                        </ZoneExplanation>
                        <ZoneExplanation>
                          {zone.id === 'power_z5' && `${zone.minPercent}% - ${zone.maxPercent}%`}
                        </ZoneExplanation>
                        <ZoneExplanation>
                          {zone.id === 'power_z6' && `${zone.minPercent}% - ${zone.maxPercent}%`}
                        </ZoneExplanation>
                        <ZoneExplanation>
                          {zone.id === 'power_z7' && `${zone.minPercent}% - ${zone.maxPercent}%`}
                        </ZoneExplanation>
                      </>
                    )}
                  </ZoneInfo>
                </ZoneItem>
              ))}
          </ZoneList>
        )}
      </Content>
    </Container>
  )
}
